import dayjs from "dayjs";
import { Task, ViewMode } from "gantt-task-react";
import { Job } from "@/hooks/services/pps/types/job-orders.type";
import { JobOrderStatus } from "./constants";
import { BadgeVariant } from "@/types/components/badge-type";

const convertStatusToNumber = (status: string) => {
  let progress = 0;
  switch (status) {
    case JobOrderStatus.NotStarted:
      progress = 0;
      break;
    case JobOrderStatus.WSI:
      progress = 10;
      break;
    case JobOrderStatus.InProgress:
      progress = 20;
      break;
    case JobOrderStatus.RepairInProgress:
      progress = 30;
      break;
    case JobOrderStatus.EvaluationInProgress:
      progress = 40;
      break;
    case JobOrderStatus.PendingARCGeneration:
      progress = 60;
      break;
    case JobOrderStatus.PendingCustomerApproval:
      progress = 65;
      break;
    case JobOrderStatus.PendingCSDQuotation:
      progress = 70;
      break;
    case JobOrderStatus.PendingSubcon:
      progress = 75;
      break;
    case JobOrderStatus.PendingMaterial:
      progress = 80;
      break;
    case JobOrderStatus.Completed:
      progress = 100;
      break;
    default:
      progress = 0;
      break;
  }

  return progress;
};

export const transformTask = (item: Job): Task | null => {
  if (item) {
    return {
      id: item.id!.toString(),
      type: "task",
      name: `${item.jobOrderNo}`,
      start: dayjs(item.startDate).toDate(),
      end: dayjs(item.dueDate).toDate(),
      progress: convertStatusToNumber(item.status.statusName),
      isDisabled: false,
      styles: {
        backgroundColor: "#ffbb54",
        backgroundSelectedColor: "#ff9e0d",
      },
      hideChildren: false,
      displayOrder: 1,
      project: item.jobOrderNo,
    };
  }

  return null;
};

export const changeVisibleDate = (view: ViewMode, setVisibleStartDate: any) => {
  const today = dayjs().toDate();

  switch (view) {
    case ViewMode.Month:
      setVisibleStartDate(dayjs(today).startOf("month").toDate());
      break;
    case ViewMode.Week:
      setVisibleStartDate(dayjs(today).startOf("week").toDate());
      break;
    case ViewMode.Day:
    case ViewMode.HalfDay:
    case ViewMode.QuarterDay:
      setVisibleStartDate(today);
      break;
    case ViewMode.Year:
      setVisibleStartDate(dayjs(today).startOf("year").toDate());
      break;
    default:
      setVisibleStartDate(today);
      break;
  }
};

export const getStartEndDateForProject = (
  tasks: Task[],
  projectId: string,
): [Date, Date] => {
  const projectTasks = tasks.filter((task) => task.project === projectId);

  if (projectTasks.length === 0) {
    throw new Error(`No tasks found for project ID: ${projectId}`);
  }

  let earliestStart = projectTasks[0].start;
  let latestEnd = projectTasks[0].end;

  for (const task of projectTasks) {
    if (task.start < earliestStart) {
      earliestStart = task.start;
    }
    if (task.end > latestEnd) {
      latestEnd = task.end;
    }
  }

  return [earliestStart, latestEnd];
};

export const mapper = (array: any[] = []) => {
  return array.map(({ field, order }) => ({ sortBy: field, order }));
};

export const tranformEvent = (item: Job) => {
  return {
    id: item.id,
    title: item.jobOrderNo,
    desc: item?.part?.partDescription,
    start: dayjs(item?.startDate).toDate(),
    end: dayjs(item?.dueDate).toDate(),
    technicians: item?.ppsTechnicians || [],
    priority: item?.priority,
    standardHours: item?.standardHours || "00",
  };
};

export const defineView = (view: string): "month" | "week" | "day" => {
  switch (view) {
    case "month":
      return "month";
    case "week":
      return "week";
    case "day":
      return "day";
    case "agenda":
      return "day";
    default:
      return "day";
  }
};

export const taskMapperItem = (
  item: any,
  formattedTechName: string,
  index?: number,
): any => {
  const { ...rest } = item;

  const id = `${item.id}-${item.jobOrderNo}-${formattedTechName || index}`;

  return {
    ...rest,
    id,
    jobOrderId: item.id,
  };
};

const textColorClasses = [
  "text-red-500",
  "text-green-500",
  "text-blue-500",
  "text-yellow-500",
  "text-purple-500",
  "text-pink-500",
  "text-indigo-500",
  "text-gray-500",
  "text-teal-500",
  "text-orange-500",
];

export function getRandomCalendarColor():
  | "sky"
  | "amber"
  | "violet"
  | "rose"
  | "emerald"
  | "orange"
  | undefined {
  const colors: ("sky" | "amber" | "violet" | "rose" | "emerald" | "orange")[] =
    ["sky", "amber", "violet", "rose", "emerald", "orange"];
  return colors[Math.floor(Math.random() * colors.length)];
}

export function getRandomTextColor(): string {
  const randomIndex = Math.floor(Math.random() * textColorClasses.length);
  return textColorClasses[randomIndex];
}

export function getVariantByStatus(status: string): BadgeVariant {
  switch (status) {
    case JobOrderStatus.NotStarted:
      return "default";
    case JobOrderStatus.JobStarted:
    case JobOrderStatus.RepairInProgress:
    case JobOrderStatus.EvaluationInProgress:
      return "warning";
    case JobOrderStatus.WSI:
      return "outline";
    case JobOrderStatus.Completed:
      return "success";
    case JobOrderStatus.PendingARCGeneration:
    case JobOrderStatus.PendingCustomerApproval:
    case JobOrderStatus.PendingCSDQuotation:
    case JobOrderStatus.PendingSubcon:
    case JobOrderStatus.PendingMaterial:
      return "destructive";
    case JobOrderStatus.InProgress:
      return "default";
    default:
      return "default";
  }
}

export const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};
